import { Container, Input, Typography } from '@app/components';
import { NO_LOGO_ID } from '@app/components/gallery-editor/components/image-item/ImageItem';
import { GalleryEditor } from '@app/components/gallery-editor/GalleryEditor';
import { useGetLibrary, useCreateLibrary, useDeleteLibrary } from '@app/domain/library/api/library.hooks.api';
import { QRCodeFormSchema } from '@app/domain/qr-code/api/qr-code.form.schema.api';
import { clsxm } from '@app/styles/clsxm';
import { ELibraryType } from '@app/swagger-types';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePersistedPublicImages } from './input/usePersistedPublicImages';
import { useEffect } from 'react';
import { COMMAND_EFFECT_REQUEST } from './qr-code-form.constants';

// TODO refactor to reuse most of logic inside via shared utils/components for both MultilinkHeaderEditor and QRCodePersonalInfo
// TODO refator to use useLibraryEditor
type QRCodeHeaderEditorProps = {
  forWidgetMode?: boolean;
  disabledEditInput?: boolean;
  prefix: 'multilink' | 'gs1' | 'wifi';
};
export const QRCodeHeaderEditor: React.FC<QRCodeHeaderEditorProps> = ({ forWidgetMode, disabledEditInput, prefix }) => {
  const { t } = useTranslation();
  const { control, watch, trigger, setValue, setFocus } = useFormContext<QRCodeFormSchema>();
  const libraryId = watch(`${prefix}.libraryId`);
  const libraryType = (() => {
    if (prefix === 'multilink') {
      return ELibraryType.MULTILINK_LOGO;
    }
    if (prefix === 'gs1') {
      return ELibraryType.GS1LOGO;
    }
    if (prefix === 'wifi') {
      return ELibraryType.WIFI_LOGO;
    }
    throw new Error('Incorect prefix');
  })();

  const { data: logos, isLoading } = useGetLibrary({ libraryType }, { forWidgetMode });

  const { mutate: createLogos, isLoading: creatingLogos } = useCreateLibrary({ forWidgetMode });
  const { mutate: deleteLogo, isLoading: deletingLogo } = useDeleteLibrary({ forWidgetMode });

  const { libraries: publicLibs, setLibraries: setPublicLibs } = usePersistedPublicImages('shared');

  const lastCommandEffectRequest = watch('lastCommandEffectRequest');
  useEffect(() => {
    if (!lastCommandEffectRequest) {
      return;
    }
    if (lastCommandEffectRequest === COMMAND_EFFECT_REQUEST.GS1_NEED_TO_ENTER_TITLE) {
      setFocus('gs1.title');
    }
  }, [setFocus, lastCommandEffectRequest]);

  const workLibs = forWidgetMode ? publicLibs : logos;
  const images = workLibs?.result.map((r) => ({ imageId: r?.file?.id, libraryId: r.id, id: r.id })) || [];
  const selectedImageId = workLibs?.result.find((l) => l.id === libraryId)?.file?.id;

  return (
    <Container className="flex flex-col gap-3 sm:gap-4 xl:rounded-xl">
      <Typography className="font-bold sm:text-l">{t('qr.multilinkInput.header')}</Typography>
      <Controller
        name={`${prefix}.title`}
        control={control}
        render={({ field: { value, onChange, ...field }, fieldState }) => (
          <Input
            {...field}
            value={value || ''}
            label={t('qr.multilinkInput.title')}
            onChange={(e) => {
              if (fieldState?.error?.message) {
                trigger(`${prefix}.title`);
              }
              onChange(e);
            }}
            withBG
            placeholder={t('qr.multilinkInput.title')}
            errorMessage={fieldState?.error?.message || ''}
            disabled={disabledEditInput}
          />
        )}
      />
      <Controller
        name={`${prefix}.description`}
        control={control}
        render={({ field: { value, onChange, ...field }, fieldState }) => (
          <Input
            {...field}
            value={value || ''}
            label={t('qr.multilinkInput.description')}
            onChange={(e) => {
              if (fieldState?.error?.message) {
                trigger(`${prefix}.description`);
              }
              onChange(e);
            }}
            withBG
            placeholder={t('qr.multilinkInput.descriptionPlaceholder')}
            errorMessage={fieldState?.error?.message || ''}
            disabled={disabledEditInput}
          />
        )}
      />
      <div className="flex flex-col gap-3">
        <Typography variant="m" className="font-bold">
          {t('qr.stepInput.imageOptional')}
        </Typography>
        <Typography variant="xxs" className="text-gray-500">
          {t('qr.stepInput.addImageDescription')}
        </Typography>

        <div className={clsxm(deletingLogo && 'pointer-events-none opacity-70')}>
          <GalleryEditor
            forWidgetMode={forWidgetMode}
            disabled={disabledEditInput}
            value={images}
            deleteOnServer={false}
            withNoImg
            dropZoneLoading={isLoading || creatingLogos}
            selectedImageId={selectedImageId || NO_LOGO_ID}
            onClickImage={(img) => {
              if (img.id === NO_LOGO_ID) {
                setValue(`${prefix}.libraryId`, NO_LOGO_ID);
                return;
              }
              const logo = workLibs?.result.find((l) => l.file?.id === img.imageId);
              if (logo) {
                setValue(`${prefix}.libraryId`, logo.id || NO_LOGO_ID);
              }
            }}
            handleUploadIds={(v) => {
              createLogos(
                { fileIds: v, libraryType },
                {
                  onSuccess: (data) => {
                    try {
                      if (forWidgetMode) {
                        setPublicLibs((prev) => {
                          if (!prev) {
                            return data;
                          }
                          const newArr = prev.result.concat(data.result);
                          return {
                            result: newArr,
                            total: newArr.length,
                          };
                        });
                      }
                      const lastUploadLogo = data.result[data.result.length - 1];
                      if (lastUploadLogo) {
                        setValue(`${prefix}.libraryId`, lastUploadLogo.id);
                      }
                    } catch {}
                  },
                }
              );
            }}
            handleDeleteId={(id) => {
              const logo = workLibs?.result?.find((l) => l?.file?.id === id);
              if (logo && logo?.id) {
                if (forWidgetMode) {
                  setPublicLibs((data) =>
                    data
                      ? {
                          ...data,
                          result: data?.result.filter((img) => img.id !== logo.id),
                        }
                      : data
                  );
                } else {
                  deleteLogo(logo.id);
                }
              }
            }}
            onChange={(v) => {
              if (!v.find((img) => img.imageId === selectedImageId && selectedImageId !== NO_LOGO_ID)) {
                setValue(`${prefix}.libraryId`, NO_LOGO_ID);
              }
            }}
            noImgText={t('qr.stepInput.noImage')}
          />
        </div>
      </div>
    </Container>
  );
};
